"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/* eslint-disable max-classes-per-file */
/* eslint-disable class-methods-use-this */
const three_1 = require("three");
const FontLoader_js_1 = require("three/examples/jsm/loaders/FontLoader.js");
const utils_3d_1 = require("../utils/utils.3d");
const BOMBallonLeader_3d_1 = __importDefault(require("./BOMBallonLeader.3d"));
const EdgeUtils_3d_1 = __importDefault(require("./EdgeUtils.3d"));
const consts_1 = require("../consts");
const utils_1 = require("../utils");
const FONT_PATHNAME = '../../../assets/fonts/OpenSans_Regular.typeface.json';
class BOMItem {
    constructor(part, itemNumber) {
        this.part = part;
        this.itemNumber = itemNumber;
    }
}
class BOMBalloonsManager {
    constructor(viewer) {
        this.minDistBetweenBallons = 4.0;
        this.lineMinLength = 2.0;
        this.cubeSize = 1.0;
        this.viewer = viewer;
        this.camera = viewer.getCamera();
        this.lineMaterial = this.createLineMaterial();
        this.arrowMaterial = this.createArrowMaterial();
        this.leaders = [];
        this.testVectorLines = [];
        this.testBBoxHelpers = [];
        this.testBBoxFromPointsHelpers = [];
        this.testCubes = [];
        this.testPointsToDraw = [];
        this.loadDefaultFont();
    }
    clear() {
        const scene = this.viewer.getScene();
        scene.remove(this.testLine);
        this.leaders.forEach((leader) => {
            leader.clear();
        });
        this.bomItems = [];
        this.testVectorLines.forEach((line) => {
            scene.remove(line);
        });
        this.testBBoxHelpers.forEach((bboxHelper) => {
            scene.remove(bboxHelper);
        });
        this.testBBoxFromPointsHelpers.forEach((bboxHelper) => {
            scene.remove(bboxHelper);
        });
        this.testCubes.forEach((cube) => {
            scene.remove(cube);
        });
        this.testPointsToDraw.forEach((points) => {
            scene.remove(points);
        });
        this.testVectorLines = [];
        this.testBBoxHelpers = [];
        this.testBBoxFromPointsHelpers = [];
        this.testCubes = [];
        this.testPointsToDraw = [];
    }
    test(bom, camera) {
        if (!bom) {
            return;
        }
        this.camera = camera;
        this.bomItems = this.getValidBOMItems(bom);
        this.calcRootBBox();
        this.drawBalloons();
    }
    createLineMaterial() {
        return new three_1.LineBasicMaterial({
            color: consts_1.consts.LEADER_COLOR,
            transparent: true,
            depthTest: false,
            opacity: 1.0,
        });
    }
    createArrowMaterial() {
        return new three_1.MeshBasicMaterial({
            color: consts_1.consts.LEADER_COLOR,
            transparent: true,
            depthTest: false,
            opacity: 1.0,
        });
    }
    calcRootBBox() {
        const scene = this.viewer.getScene();
        this.rootBBox = new three_1.Box3();
        scene.children.forEach((child) => {
            if (this.isPart(child)) {
                this.rootBBox.expandByObject(child);
            }
        });
    }
    getVertices(geometry, matrix) {
        const positionAttribute = geometry.attributes.position;
        const coords = positionAttribute.array;
        const vertices = [];
        for (let vertexIndex = 0; vertexIndex < coords.length; vertexIndex += positionAttribute.itemSize) {
            const pos = new three_1.Vector3(coords[vertexIndex], coords[vertexIndex + 1], coords[vertexIndex + 2]);
            pos.applyMatrix4(matrix);
            vertices.push(pos);
        }
        return vertices;
    }
    getMeshVertices(mesh) {
        return this.getVertices(mesh.geometry, mesh.matrixWorld);
    }
    getBBoxVertices(bbox) {
        const bboxHelper = new three_1.Box3Helper(bbox);
        bboxHelper.updateMatrixWorld();
        return this.getVertices(bboxHelper.geometry, bboxHelper.matrix);
    }
    projectBBoxOnCameraPlane(bbox, coplanarPoint) {
        const vertices = this.getBBoxVertices(bbox);
        return vertices.map((vertex) => this.projectPointOnCameraPlane(vertex, coplanarPoint));
    }
    getCameraDirection() {
        const cameraDirection = new three_1.Vector3();
        this.camera.getWorldDirection(cameraDirection);
        return cameraDirection;
    }
    projectPointOnCameraPlane(point, coplanarPoint) {
        const cameraDirection = this.getCameraDirection();
        const plane = new three_1.Plane();
        plane.setFromNormalAndCoplanarPoint(cameraDirection, coplanarPoint);
        const projectedPoint = new three_1.Vector3();
        plane.projectPoint(point, projectedPoint);
        return projectedPoint;
    }
    getConvexHull(points, planeNormal) {
        const cameraPosition = new three_1.Vector3();
        this.camera.getWorldPosition(cameraPosition);
        const hull = [];
        const startPoint = this.findFurthestPoint(cameraPosition, points);
        const prevPoint = startPoint.clone();
        const defaultVector = new three_1.Vector3();
        const candidate = defaultVector.clone();
        while (!candidate.equals(startPoint)) {
            candidate.copy(defaultVector);
            points.forEach((point) => {
                if (point.equals(prevPoint)) {
                    return;
                }
                if (candidate.equals(defaultVector)) {
                    candidate.copy(point);
                    return;
                }
                const firstVec = candidate.clone().sub(prevPoint);
                const secondVec = point.clone().sub(prevPoint);
                const crossProduct = firstVec.clone().cross(secondVec);
                const crossProductLength = crossProduct.length();
                if ((0, utils_3d_1.areEqual)(crossProductLength, 0.0) && firstVec.length() < secondVec.length()) {
                    candidate.copy(point);
                    return;
                }
                if (planeNormal.clone().dot(crossProduct) < 0.0) {
                    candidate.copy(point);
                }
            });
            hull.push(candidate.clone());
            prevPoint.copy(candidate);
        }
        return hull;
    }
    findFurthestPoint(startPoint, points) {
        let longestDistance = 0;
        let furthestPoint = startPoint;
        for (let pointIndex = 0; pointIndex < points.length; pointIndex += 1) {
            const distance = startPoint.distanceTo(points[pointIndex]);
            if (longestDistance < distance) {
                longestDistance = distance;
                furthestPoint = points[pointIndex];
            }
        }
        return furthestPoint;
    }
    getValidBOMItems(bom) {
        const validBOMItems = [];
        if (!bom.groups) {
            return validBOMItems;
        }
        const extrusionGroup = bom.groups.filter((group) => group.name.toLowerCase() === 'extrusions')[0];
        const componentGroup = bom.groups.filter((group) => group.name.toLowerCase() === 'components')[0];
        const groups = [extrusionGroup, componentGroup];
        let itemIndex = 1;
        groups.forEach((group) => {
            if (!group) {
                return;
            }
            group.items.forEach((item) => {
                const [itemId] = (item === null || item === void 0 ? void 0 : item.uuid) || [];
                if (!itemId) {
                    return;
                }
                let part = this.viewer.getObjectById(itemId);
                if (!part) {
                    return;
                }
                if (utils_1.Utils.isConnection(part)) {
                    [part] = part.getFasteners();
                    if (!part) {
                        return;
                    }
                }
                if (!this.isObjectVisible(part)) {
                    itemIndex += 1;
                    return;
                }
                const bomItem = new BOMItem(part, itemIndex);
                validBOMItems.push(bomItem);
                itemIndex += 1;
            });
        });
        return validBOMItems;
    }
    isPart(object) {
        const scalingToolObject = this.viewer.getDesignProMain().scalingTool.getObject();
        const baseObject = object;
        return baseObject && baseObject.isCustomObject && baseObject.uuid !== (scalingToolObject === null || scalingToolObject === void 0 ? void 0 : scalingToolObject.uuid);
    }
    // VARIANT 1
    drawBalloons() {
        const rootBBoxCenter = new three_1.Vector3();
        this.rootBBox.getCenter(rootBBoxCenter);
        const pointsOnEdges = [];
        const cameraDirection = this.getCameraDirection();
        const projectedBBoxVertices = this.projectBBoxOnCameraPlane(this.rootBBox, rootBBoxCenter);
        const convexHullPoints = this.getConvexHull(projectedBBoxVertices, cameraDirection);
        const edges = EdgeUtils_3d_1.default.createEdgesFromPoints(convexHullPoints);
        // Using for debugging
        // this.testDrawLineLoopFromPoints(convexHullPoints);
        this.bomItems.forEach((bomItem) => {
            const referencePoint = this.getReferencePoint(bomItem.part);
            if (!referencePoint) {
                return;
            }
            const projectedReferencePoint = this.projectPointOnCameraPlane(referencePoint, rootBBoxCenter);
            const pointOnClosestEdge = this.getPointOnClosestEdge(edges, projectedReferencePoint, pointsOnEdges);
            if (!pointOnClosestEdge) {
                return;
            }
            const leader = new BOMBallonLeader_3d_1.default(this.viewer, this.lineMaterial, this.arrowMaterial, bomItem.itemNumber.toString(), this.defaultFont);
            leader.draw(pointOnClosestEdge, projectedReferencePoint);
            this.leaders.push(leader);
            pointsOnEdges.push(pointOnClosestEdge);
        });
    }
    // VARIANT 2
    drawBalloonsV2() {
        const rootBBoxCenter = new three_1.Vector3();
        this.rootBBox.getCenter(rootBBoxCenter);
        const pointsOnEdges = [];
        this.bomItems.forEach((bomItem) => {
            const referencePoint = this.getReferencePoint(bomItem.part);
            if (!referencePoint) {
                return;
            }
            const cameraDirection = this.getCameraDirection();
            const projectedBBoxVertices = this.projectBBoxOnCameraPlane(this.rootBBox, referencePoint);
            const convexHullPoints = this.getConvexHull(projectedBBoxVertices, cameraDirection);
            const edges = EdgeUtils_3d_1.default.createEdgesFromPoints(convexHullPoints);
            const pointOnClosestEdge = this.getPointOnClosestEdge(edges, referencePoint, pointsOnEdges);
            if (!pointOnClosestEdge) {
                return;
            }
            const leader = new BOMBallonLeader_3d_1.default(this.viewer, this.lineMaterial, this.arrowMaterial, bomItem.itemNumber.toString(), this.defaultFont);
            leader.draw(pointOnClosestEdge, referencePoint);
            this.leaders.push(leader);
            pointsOnEdges.push(pointOnClosestEdge);
        });
    }
    getPointOnClosestEdge(edges, referencePoint, otherPointsOnEdges) {
        let pointOnClosestEdge = null;
        let shortestDistance = null;
        edges.forEach((edge) => {
            const pointOnEdge = this.getPointOnEdge(edge, referencePoint);
            const distanceToPointOnEdge = referencePoint.distanceTo(pointOnEdge);
            const isShorterDistance = !shortestDistance || distanceToPointOnEdge < shortestDistance;
            if (!isShorterDistance || !this.isPointOnEdgeValid(pointOnEdge, otherPointsOnEdges)) {
                return;
            }
            shortestDistance = distanceToPointOnEdge;
            pointOnClosestEdge = pointOnEdge;
        });
        return pointOnClosestEdge;
    }
    getPointOnEdge(edge, referencePoint) {
        const pointOnEdge = EdgeUtils_3d_1.default.getPointOnEdge(edge, referencePoint);
        const lineDirection = pointOnEdge.clone().sub(referencePoint);
        const lineLength = lineDirection.length();
        if (lineLength > this.lineMinLength || (0, utils_3d_1.areEqual)(lineLength, this.lineMinLength)) {
            return pointOnEdge;
        }
        return referencePoint.clone().add(lineDirection.normalize().multiplyScalar(this.lineMinLength));
    }
    isPointOnEdgeValid(pointOnEdge, otherPointsOnEdges) {
        if (!this.isPointVisible(pointOnEdge)) {
            return false;
        }
        const rootBBoxCenter = new three_1.Vector3();
        this.rootBBox.getCenter(rootBBoxCenter);
        const projectedPointOnEdge = this.projectPointOnCameraPlane(pointOnEdge, rootBBoxCenter);
        const tooClosePoints = otherPointsOnEdges.filter((otherPoint) => {
            const projectedOtherPoint = this.projectPointOnCameraPlane(otherPoint, rootBBoxCenter);
            return projectedPointOnEdge.distanceTo(projectedOtherPoint) < this.minDistBetweenBallons;
        });
        if (tooClosePoints.length > 0) {
            return false;
        }
        return true;
    }
    isPointVisible(point) {
        const frustum = this.getCameraFrustum();
        return frustum.containsPoint(point);
    }
    getCameraFrustum() {
        const frustum = new three_1.Frustum();
        const { projectionMatrix, matrixWorldInverse } = this.camera;
        frustum.setFromProjectionMatrix(new three_1.Matrix4().multiplyMatrices(projectionMatrix, matrixWorldInverse));
        return frustum;
    }
    isObjectVisible(object) {
        var _a;
        if (!((_a = object.outerBox) === null || _a === void 0 ? void 0 : _a.geometry)) {
            return false;
        }
        const frustum = this.getCameraFrustum();
        return frustum.intersectsObject(object.outerBox);
    }
    getReferencePoint(part) {
        var _a;
        const bbox = (_a = part.mesh.geometry.boundingBox) === null || _a === void 0 ? void 0 : _a.clone();
        if (!bbox) {
            return null;
        }
        const partCenter = new three_1.Vector3();
        bbox.getCenter(partCenter);
        part.localToWorld(partCenter);
        if (this.isPointVisible(partCenter)) {
            return partCenter;
        }
        const outerBoxVertices = this.getMeshVertices(part.outerBox);
        const visibleVertices = outerBoxVertices.filter((vertex) => this.isPointVisible(vertex));
        if (visibleVertices.length === 0) {
            return null;
        }
        return visibleVertices[0];
    }
    /* private labelOverlapsAnyPart(labelCenter: Vector3): boolean {
      const cube = this.createCube();
      // cube.position.copy(labelCenter);
      cube.position.copy(new Vector3(0.0, 0.0, 0.0));
      // console.log('cube before update matrix world', cube);
      // cube.updateMatrixWorld();
      // console.log('cube after update matrix world', cube);
      // cube.geometry.computeBoundingBox();
      // const cubeBBoxTest = cube.geometry.boundingBox?.clone();
      const cubeBBox = new Box3();
      cubeBBox.expandByObject(cube);
  
      if (!cubeBBox) {
        return true;
      }
  
      const intersectedParts = this.bomItems.filter((item) => {
        const partBBox = new Box3();
        partBBox.expandByObject(item.part);
  
        if (!partBBox) {
          return true;
        }
  
        // this.testDrawBBox(partBBox, new Color(0xff0000));
        // return !cubeBBox.clone().intersect(partBBox).isEmpty();
        return this.boxesOverlap(partBBox, cubeBBox);
      });
  
      return intersectedParts.length > 0;
    } */
    createCube() {
        const geometry = new three_1.BoxGeometry(this.cubeSize, this.cubeSize, this.cubeSize);
        const material = new three_1.MeshBasicMaterial({ color: 0xffff00 });
        return new three_1.Mesh(geometry, material);
    }
    boxesOverlap(firstBBox, secondBBox) {
        const firstBBoxCenter = new three_1.Vector3();
        firstBBox.getCenter(firstBBoxCenter);
        const cameraDirection = this.getCameraDirection();
        const projectedFirstBBoxVertices = this.projectBBoxOnCameraPlane(firstBBox, firstBBoxCenter);
        const projectedSecondBBoxVertices = this.projectBBoxOnCameraPlane(secondBBox, firstBBoxCenter);
        const firstBBoxConvexHull = this.getConvexHull(projectedFirstBBoxVertices, cameraDirection);
        const secondBBoxConvexHull = this.getConvexHull(projectedSecondBBoxVertices, cameraDirection);
        return this.convexHullsOverlap(firstBBoxConvexHull, secondBBoxConvexHull, cameraDirection);
    }
    convexHullsOverlap(firstHull, secondHull, planeNormal) {
        const hulls = [firstHull, secondHull];
        const hullsSeparatingAxes = [];
        hulls.forEach((hull) => {
            for (let hullIndex = 0; hullIndex < hull.length - 1; hullIndex += 1) {
                const separatingAxis = this.getSeparatingAxis(hull[hullIndex], hull[hullIndex + 1], planeNormal);
                hullsSeparatingAxes.push(separatingAxis);
            }
            const lastSeparatingAxis = this.getSeparatingAxis(hull.at(-1), hull[0], planeNormal);
            hullsSeparatingAxes.push(lastSeparatingAxis);
        });
        const validSeparatingAxes = hullsSeparatingAxes.filter((separatingAxis) => {
            const firstHullBBox = new three_1.Box3();
            const secondHullBBox = new three_1.Box3();
            firstHull.forEach((point) => firstHullBBox.expandByPoint(point.projectOnVector(separatingAxis)));
            secondHull.forEach((point) => secondHullBBox.expandByPoint(point.projectOnVector(separatingAxis)));
            const firstHullProjectedPoints = firstHull.map((point) => point.projectOnVector(separatingAxis));
            const secondHullProjectedPoints = firstHull.map((point) => point.projectOnVector(separatingAxis));
            this.testDrawPoints(firstHullProjectedPoints, new three_1.Color(0xff0000));
            this.testDrawPoints(secondHullProjectedPoints, new three_1.Color(0xffff00));
            return !((firstHullBBox.min < secondHullBBox.max && firstHullBBox.min > secondHullBBox.min) ||
                (secondHullBBox.min < firstHullBBox.max && secondHullBBox.min > firstHullBBox.min));
        });
        return validSeparatingAxes.length > 0;
    }
    getSeparatingAxis(firstPoint, secondPoint, planeNormal) {
        const pointBetween = firstPoint.clone().add(secondPoint).multiplyScalar(0.5);
        const edgeVector = secondPoint.clone().sub(firstPoint);
        this.testDrawLine(firstPoint, secondPoint, new three_1.Color(0x00ff00));
        const separatingAxis = edgeVector.clone().cross(planeNormal);
        this.testDrawVector(pointBetween, separatingAxis, 5, new three_1.Color(0x0000ff));
        return edgeVector.clone().cross(planeNormal);
    }
    testDrawCube(cube, position) {
        if (!position) {
            return;
        }
        const cubeToDraw = cube.clone();
        cubeToDraw.position.copy(position);
        this.viewer.getScene().add(cubeToDraw);
        this.testCubes.push(cubeToDraw);
    }
    testDrawBBoxFromPoints(points) {
        const bbox = new three_1.Box3();
        bbox.setFromPoints(points);
        const bboxFromPointsHelper = new three_1.Box3Helper(bbox);
        this.viewer.getScene().add(bboxFromPointsHelper);
        this.testBBoxFromPointsHelpers.push(bboxFromPointsHelper);
    }
    testDrawBBox(bbox, color = new three_1.Color(0xffff00)) {
        if (!bbox) {
            return;
        }
        const bboxHelper = new three_1.Box3Helper(bbox, color);
        this.viewer.getScene().add(bboxHelper);
        this.testBBoxHelpers.push(bboxHelper);
    }
    testDrawLineLoopFromPoints(points) {
        const material = new three_1.LineBasicMaterial({ color: 0xffff00 });
        const geometry = new three_1.BufferGeometry().setFromPoints(points);
        const scene = this.viewer.getScene();
        scene.remove(this.testLine);
        this.testLine = new three_1.LineLoop(geometry, material);
        scene.add(this.testLine);
    }
    testDrawPoints(points, color = new three_1.Color(0xff0000)) {
        const geometry = new three_1.BufferGeometry().setFromPoints(points);
        const material = new three_1.PointsMaterial({ color, transparent: true, depthTest: false });
        const scene = this.viewer.getScene();
        const pointsToDraw = new three_1.Points(geometry, material);
        scene.add(pointsToDraw);
        this.testPointsToDraw.push(pointsToDraw);
    }
    testDrawLine(startPoint, endPoint, color = new three_1.Color(0xff0000)) {
        const scene = this.viewer.getScene();
        const material = new three_1.LineBasicMaterial({ color });
        const geometry = new three_1.BufferGeometry().setFromPoints([startPoint, endPoint]);
        const line = new three_1.Line(geometry, material);
        scene.add(line);
        this.testVectorLines.push(line);
    }
    testDrawVector(startPoint, direction, length, color = new three_1.Color(0xff0000)) {
        const scene = this.viewer.getScene();
        const material = new three_1.LineBasicMaterial({ color });
        const endPoint = startPoint.clone().add(direction.clone().normalize().multiplyScalar(5));
        const geometry = new three_1.BufferGeometry().setFromPoints([startPoint, endPoint]);
        const line = new three_1.Line(geometry, material);
        scene.add(line);
        this.testVectorLines.push(line);
    }
    loadDefaultFont() {
        return __awaiter(this, void 0, void 0, function* () {
            const fontLoader = new FontLoader_js_1.FontLoader();
            this.defaultFont = yield fontLoader.loadAsync(FONT_PATHNAME);
        });
    }
}
exports.default = BOMBalloonsManager;
